import { useState } from 'react'

const ConnectionTest = () => {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)

  const testConnection = async (url, label) => {
    setLoading(true)
    try {
      console.log(`Testing ${label}: ${url}`)
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      
      const data = await response.text()
      console.log(`${label} response:`, response.status, data)
      
      setResults(prev => [...prev, {
        label,
        url,
        status: response.status,
        success: response.ok,
        data: data,
        timestamp: new Date().toLocaleString()
      }])
    } catch (error) {
      console.error(`${label} error:`, error)
      setResults(prev => [...prev, {
        label,
        url,
        status: 'ERROR',
        success: false,
        data: error.message,
        timestamp: new Date().toLocaleString()
      }])
    }
    setLoading(false)
  }

  const testRegistration = async () => {
    setLoading(true)
    try {
      const testUser = {
        username: 'testuser' + Date.now(),
        email: 'test' + Date.now() + '@example.com',
        password: 'password123',
        full_name: 'Test User'
      }
      
      console.log('Testing registration with:', testUser)
      
      const response = await fetch('http://localhost:5000/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testUser)
      })
      
      const data = await response.text()
      console.log('Registration response:', response.status, data)
      
      setResults(prev => [...prev, {
        label: 'Registration Test',
        url: 'http://localhost:5000/api/auth/register',
        status: response.status,
        success: response.ok,
        data: data,
        timestamp: new Date().toLocaleString()
      }])
    } catch (error) {
      console.error('Registration error:', error)
      setResults(prev => [...prev, {
        label: 'Registration Test',
        url: 'http://localhost:5000/api/auth/register',
        status: 'ERROR',
        success: false,
        data: error.message,
        timestamp: new Date().toLocaleString()
      }])
    }
    setLoading(false)
  }

  const testLogin = async () => {
    setLoading(true)
    try {
      const credentials = {
        username: 'admin',
        password: 'admin123'
      }
      
      console.log('Testing login with:', credentials)
      
      const response = await fetch('http://localhost:5000/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials)
      })
      
      const data = await response.text()
      console.log('Login response:', response.status, data)
      
      setResults(prev => [...prev, {
        label: 'Login Test',
        url: 'http://localhost:5000/api/auth/login',
        status: response.status,
        success: response.ok,
        data: data,
        timestamp: new Date().toLocaleString()
      }])
    } catch (error) {
      console.error('Login error:', error)
      setResults(prev => [...prev, {
        label: 'Login Test',
        url: 'http://localhost:5000/api/auth/login',
        status: 'ERROR',
        success: false,
        data: error.message,
        timestamp: new Date().toLocaleString()
      }])
    }
    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Connection Test</h1>
        
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Test Connections</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button 
              onClick={() => testConnection('http://localhost:5000/', 'Simple Server')}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              disabled={loading}
            >
              Test Simple Server (Port 5000)
            </button>
            <button 
              onClick={() => testConnection('http://localhost:5000/', 'Main Server Root')}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              disabled={loading}
            >
              Test Main Server Root (Port 5000)
            </button>
            <button 
              onClick={() => testConnection('http://localhost:5000/api/health', 'Health Check')}
              className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
              disabled={loading}
            >
              Test Health Check
            </button>
            <button 
              onClick={testRegistration}
              className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
              disabled={loading}
            >
              Test Registration
            </button>
            <button 
              onClick={testLogin}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              disabled={loading}
            >
              Test Login (admin/admin123)
            </button>
            <button 
              onClick={() => setResults([])}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Clear Results
            </button>
          </div>
          {loading && <p className="mt-4 text-blue-600">Testing connection...</p>}
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Test Results</h2>
          {results.length === 0 ? (
            <p className="text-gray-500">No tests run yet. Click a button above to test connections.</p>
          ) : (
            <div className="space-y-4">
              {results.map((result, index) => (
                <div 
                  key={index} 
                  className={`p-4 rounded-lg border ${result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold">{result.label}</h3>
                    <span className={`px-2 py-1 rounded text-sm ${result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {result.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">URL: {result.url}</p>
                  <p className="text-sm text-gray-600 mb-2">Time: {result.timestamp}</p>
                  <div className="bg-gray-100 p-2 rounded text-sm font-mono">
                    {result.data}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ConnectionTest